import React, { useMemo } from 'react';
import { Flex, Skeleton, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Device } from 'clipsal-cortex-types/src/api';

import { useGetFirmwareVersion } from '../../accountApi';

export function DetailsInfo({ device }: { device: Device }) {
  const { t } = useTranslation();

  const hasFirmware = false;

  const { data, isLoading } = useGetFirmwareVersion(Number(device.id), !hasFirmware);

  const { firmware, manufacturer, model, serial } = useMemo(() => {
    const deviceSerial = device.serial_number ?? device.site_identifier;
    const deviceFirmware = hasFirmware ? data?.firmware_version : undefined;

    return {
      serial: deviceSerial ?? undefined,
      model: device.model,
      manufacturer: device ? device.manufacturer_name ?? 'Schneider Electric' : undefined,
      firmware: deviceFirmware ?? undefined,
    };
  }, [device, hasFirmware, data?.firmware_version]);

  return (
    <Flex direction={'column'} w="100%" data-testid="device-details" mt={8}>
      <Flex direction={'column'}>
        {serial && (
          <Flex direction={'column'} data-testid="device-sn">
            <Text fontWeight={'semibold'}>{t('Common.serial number')}</Text>
            <Text>{serial}</Text>
          </Flex>
        )}

        {model && (
          <Flex direction={'column'} mt={6} data-testid="device-model">
            <Text fontWeight={'semibold'}>{t('Common.model name')}</Text>
            <Text>{model}</Text>
          </Flex>
        )}

        {manufacturer && (
          <Flex direction={'column'} mt={6} data-testid="device-manufacturer">
            <Text fontWeight={'semibold'}>{t('Hardware.manufacturer')}</Text>
            <Text>{manufacturer}</Text>
          </Flex>
        )}

        {/* Firmware version display */}
        {firmware && (
          <Flex direction={'column'} mt={6} data-testid="device-firmware">
            <Text fontWeight={'semibold'}>{t('Common.firmware')}</Text>
            <Skeleton isLoaded={!isLoading}>
              <Text>{firmware}</Text>
            </Skeleton>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
